import { useQueryClient } from '@tanstack/react-query';
import { union } from 'es-toolkit';
// mantine
import { Button } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { modals } from '@mantine/modals';
// icons
import { Download } from 'lucide-react';
// components
import type { ITopic } from '@newo/api/gateway';
import ImportAKBForm from '@/pages/AKB/components/Toolbar/components/ImportAKBButton/components/ImportAKBForm';
// utils
import { useImportAKBMutation } from '@/services/akb/akb.queries.ts';
import { readFileAsText } from '@/utils';

interface ImportAKBButtonProps {
    className?: string;
    personaId: string;
    isDisabled: boolean;
}

const handleCloseAll = () => {
    modals.closeAll();
};

const ImportAKBButton = ({
    className,
    personaId,
    isDisabled
}: ImportAKBButtonProps) => {
    const queryClient = useQueryClient();
    const { mutate, isPending } = useImportAKBMutation();

    const handleSubmit = async ({
        file,
        labels
    }: {
        file: File;
        labels: string[];
    }) => {
        try {
            const data = await readFileAsText(file);
            const jsonData = JSON.parse(data) as ITopic[];
            const extendedData = jsonData.map((topic: ITopic) => ({
                ...topic,
                labels: union(topic.labels, labels)
            }));
            mutate(
                { personaId, data: extendedData },
                {
                    onSuccess() {
                        queryClient.invalidateQueries({
                            queryKey: ['akb/topics']
                        });
                    }
                }
            );
        } catch (error) {
            const { message } = error as Error;
            notifications.show({
                color: 'red',
                title: 'Error',
                message,
                autoClose: 5000
            });
        }
    };

    const handleClick = () => {
        modals.open({
            title: 'Import AKB',
            size: '50%',
            children: (
                <ImportAKBForm
                    onCancel={handleCloseAll}
                    onSubmit={handleSubmit}
                />
            )
        });
    };

    return (
        <Button
            className={className}
            disabled={isDisabled}
            leftSection={<Download />}
            loading={isPending}
            variant="default"
            onClick={handleClick}
        >
            Import AKB
        </Button>
    );
};

export default ImportAKBButton;
